import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Activity, BarChart3, ChevronLeft, ChevronRight, TrendingUp, Users } from 'lucide-react';
import { fetchAdminAnalytics, type AdminAnalyticsActivity } from '../../api/admin';
import {
  AdminDetailCard,
  AdminEmptyState,
  AdminKpiStrip,
  AdminPageLayout,
  AdminStatusBadge,
  AdminTablePanel,
} from '../../components/admin/AdminPageLayout';
import { LoadingState, ErrorState } from '../../components/states/StateViews';

function ActivityDetailCard({ activity, onClose }: { activity: AdminAnalyticsActivity | null; onClose: () => void }) {
  return (
    <AdminDetailCard open={Boolean(activity)} onClose={onClose} ariaLabel="Activity details" breadcrumb="Activity event" title={activity?.label ?? ''} illustration="admin.panel.analytics"
      subtitle={activity ? (<><AdminStatusBadge status={activity.type} /><span className="wfx-muted wfx-admin-subline">{new Date(activity.timestamp).toLocaleString()}</span></>) : undefined}>
      {activity && (
        <dl className="wfx-admin-meta-list wfx-admin-meta-list--grid">
          <div><dt>Type</dt><dd>{activity.type === 'registration' ? 'User registration' : 'Notification sent'}</dd></div>
          <div><dt>Detail</dt><dd>{activity.detail ?? '—'}</dd></div>
          <div><dt>Meta</dt><dd>{activity.meta ?? '—'}</dd></div>
          <div><dt>Timestamp</dt><dd>{new Date(activity.timestamp).toLocaleString()}</dd></div>
        </dl>
      )}
    </AdminDetailCard>
  );
}

function ActivityCard({ row, onSelect }: { row: AdminAnalyticsActivity; onSelect: () => void }) {
  return (
    <button type="button" className="wfx-admin-analytics-card glass-surface" onClick={onSelect}>
      <AdminStatusBadge status={row.type} />
      <strong>{row.label}</strong>
      <span className="wfx-muted">{row.detail ?? row.meta}</span>
      <span className="wfx-muted wfx-admin-subline">{new Date(row.timestamp).toLocaleString()}</span>
    </button>
  );
}

export function AdminAnalyticsScreen() {
  const [period, setPeriod] = useState('7d');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<AdminAnalyticsActivity | null>(null);

  useEffect(() => { setPage(1); }, [period]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-analytics', period, page],
    queryFn: () => fetchAdminAnalytics({ period, page, limit: 12 }),
  });

  const pagination = data?.pagination;
  const activity = data?.activity ?? [];
  const kpiItems = useMemo(() => [
    { label: 'Total users', value: data?.users.total ?? 0 },
    { label: 'Registrations today', value: data?.users.today ?? 0 },
    { label: 'Engagement score', value: `${data?.engagement.score ?? 0}%` },
    { label: 'Alerts in period', value: data?.content.notificationsInPeriod ?? 0 },
  ], [data]);

  if (isLoading && !data) return <LoadingState label="Loading analytics…" />;
  if (isError) return <ErrorState message="Could not load analytics." onRetry={() => void refetch()} />;

  return (
    <>
      <AdminPageLayout breadcrumb="Admin / Analytics" title="Analytics" description="Track user growth, content engagement, and platform activity." icon={BarChart3} illustration="admin.panel.analytics">
        <AdminKpiStrip items={kpiItems} />
        <div className="wfx-admin-toolbar wfx-admin-analytics-toolbar">
          <select className="wfx-admin-select" value={period} onChange={(e) => setPeriod(e.target.value)} aria-label="Time period">
            <option value="7d">Last 7 days</option><option value="30d">Last 30 days</option><option value="all">All time</option>
          </select>
        </div>
        <div className="wfx-admin-grid-two">
          <article className="wfx-admin-panel glass-surface">
            <h2><TrendingUp size={18} /> Traffic {data?.traffic.estimated && <span className="wfx-muted wfx-admin-subline">(estimated)</span>}</h2>
            <div className="wfx-admin-stat-row">
              <div><strong>{data?.traffic.today}</strong><span>Today</span></div>
              <div><strong>{data?.traffic.yesterday}</strong><span>Yesterday</span></div>
              <div><strong>{data?.traffic.lastWeek}</strong><span>Last week</span></div>
            </div>
            <ul className="wfx-admin-note-list">{data?.traffic.sources.map((s) => <li key={s.label}>{s.label}: {s.value}%</li>)}</ul>
          </article>
          <article className="wfx-admin-panel glass-surface">
            <h2><Users size={18} /> Content snapshot</h2>
            <dl className="wfx-admin-meta-list">
              <div><dt>Exam results</dt><dd>{data?.content.results}</dd></div>
              <div><dt>Open opportunities</dt><dd>{data?.content.openOpportunities}</dd></div>
              <div><dt>Vault documents</dt><dd>{data?.content.vaultDocuments}</dd></div>
              <div><dt>Published blogs</dt><dd>{data?.content.publishedBlogs}</dd></div>
            </dl>
          </article>
        </div>
        <article className="wfx-admin-panel glass-surface">
          <h2><Activity size={18} /> Top modules</h2>
          <div className="wfx-admin-analytics-modules">
            {data?.topModules.map((m) => (
              <div key={m.label} className="wfx-admin-analytics-module"><span>{m.label}</span><div className="wfx-admin-analytics-bar"><span style={{ width: `${m.value}%` }} /></div><strong>{m.value}%</strong></div>
            ))}
          </div>
        </article>
        <div className="wfx-admin-blog-grid">{activity.map((row) => <ActivityCard key={row.id} row={row} onSelect={() => setSelected(row)} />)}</div>
        {activity.length === 0 && <AdminEmptyState message="No activity in this period." />}
        <AdminTablePanel title="Recent activity" footer={pagination && pagination.pages > 1 ? (
          <div className="wfx-admin-pagination">
            <button type="button" className="secondary-action compact" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}><ChevronLeft size={16} /> Previous</button>
            <span className="wfx-muted">Page {pagination.page} of {pagination.pages} · {pagination.total} events</span>
            <button type="button" className="secondary-action compact" disabled={page >= pagination.pages} onClick={() => setPage((p) => p + 1)}>Next <ChevronRight size={16} /></button>
          </div>
        ) : undefined}>
          <table className="wfx-admin-table"><thead><tr><th>Event</th><th>Type</th><th>Detail</th><th>Time</th></tr></thead>
            <tbody>{activity.map((row) => (
              <tr key={row.id} className="wfx-admin-table-row-clickable" onClick={() => setSelected(row)}>
                <td><strong>{row.label}</strong></td><td><AdminStatusBadge status={row.type} /></td><td>{row.detail ?? row.meta ?? '—'}</td><td>{new Date(row.timestamp).toLocaleString()}</td>
              </tr>
            ))}</tbody>
          </table>
        </AdminTablePanel>
      </AdminPageLayout>
      <ActivityDetailCard activity={selected} onClose={() => setSelected(null)} />
    </>
  );
}
